import { InputAdornment, Stack, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Input } from "components";
import Decimal from "decimal.js";

const schema = yup.object().shape({
  tax_name: yup.string().required("Name is required"),
  tax_rate: yup
    .number()
    .required("Tax rate is required")
    .test("percentage", "Tax rate must be greater than 0", (value) => {
      return value && value > 0 ? true : false;
    })
    .typeError("Tax rate is required")
    .max(100000, "Tax rate must be less than 1000"),
});

interface SingleTaxTabProps {
  mode?: {
    method: "create" | "update";
    data: any;
    primaryButtonDisable: boolean;
  };
}

export const SingleTaxTab = forwardRef((props: SingleTaxTabProps, ref) => {
  const { mode } = props;
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      tax_name: "",
      tax_rate: 0,
    },
  });

  useEffect(() => {
    if (mode?.method === "update") {
      reset({
        tax_name: mode?.data?.tax_name,
        tax_rate: mode?.data?.tax_rate,
      });
    } else {
      reset({
        tax_name: "",
        tax_rate: 0,
      });
    }
  }, [mode]);

  const onSubmitHandler = async () => {
    let newData;
    await handleSubmit((data) => {
      newData = data;
    })();
    return newData;
  };

  useImperativeHandle(ref, () => ({
    onSubmit: onSubmitHandler,
    // isLoading: () => {
    //   return false;
    // },
  }));

  return (
    <Stack
      sx={{
        gap: "16px",
      }}
    >
      <Typography variant="subtitle1">Single tax</Typography>
      <Input
        name="tax_name"
        label="Tax Label"
        control={control}
        error={!!errors.tax_name}
        errorMessage={errors.tax_name?.message}
        inputProps={{
          maxLength: 50,
        }}
      />
      <Controller
        control={control}
        name="tax_rate"
        render={({ field }) => (
          <Input
            {...field}
            value={
              field.value
                ? new Decimal(field.value).div(100).toNumber()
                : field.value
            }
            onBlur={(e) => {
              if (
                e.target.value &&
                new Decimal(e.target.value).toNumber() > 100
              ) {
                field.onChange(10000);
              }
            }}
            digitsAfterDecimal={3}
            onChange={(e) => {
              if (
                e.target.value &&
                new Decimal(e.target.value).toNumber() > 100
              ) {
                e.target.value = "100";
              }
              field.onChange(
                e.target.value
                  ? new Decimal(e.target.value).mul(100).toNumber()
                  : e.target.value,
              );
            }}
            label="Tax Rate (%)"
            error={!!errors.tax_rate}
            errorMessage={errors.tax_rate?.message}
            type="number"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{
              min: 0,
              step: 0.1,
            }}
            onKeyDown={(e) => {
              if (
                e.key === "e" ||
                e.key === "E" ||
                e.key === "+" ||
                e.key === "-"
              ) {
                e.preventDefault();
              }
            }}
          />
        )}
      />
    </Stack>
  );
});
